import defaultTheme from 'tailwindcss/defaultTheme';
import tailwindForms from '@tailwindcss/forms';
import tailwindCss3d from 'tailwindcss-3d';
import typography from '@tailwindcss/typography';
import { gpxPorto } from '@gpx-spa/ui/tokens/brand-colors';
import tailwindcssPrimeui from '@gpx-spa/ui/tailwindcss-primeui';

export default {
  content: [
    './public/**/*.html',
    './src/**/*.{vue,js,ts,jsx,tsx}',
    '../../packages/ui/src/**/*.{vue,js,ts,jsx,tsx}',
    '../../packages/constants/**/*.ts',
  ],
  // @tailwindcss/forms with strategy: 'class' emits .form-* utilities; safelist so JIT keeps them
  // when not yet referenced in templates (native fields opt in explicitly).
  safelist: [
    'form-input',
    'form-textarea',
    'form-select',
    'form-multiselect',
    'form-checkbox',
    'form-radio',
  ],
  theme: {
    screens: {
      xs: '640px',
      sm: '768px',
      md: '1024px',
      lg: '1280px',
      xl: '1440px',
    },
    fontFamily: {
      sans: ['Roobert', ...defaultTheme.fontFamily.sans],
      'sans-inter': [
        'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
      ],
      mono: defaultTheme.fontFamily.mono,
    },
    extend: {
      boxShadow: {
        activeBtn: 'inset 0 3px 5px rgb(0 0 0 / 13%)',
        btn: 'inset 0 1px 0 rgb(255 255 255 / 15%), 0 1px 1px rgb(0 0 0 / 8%);',
        progress: 'inset 0 0.1rem 0.1rem rgb(0 0 0 / 10%)',
      },
      colors: {
        'bg-1': '#F1F3F5',
        'bg-2': '#F3F1F8',
        'bg-3': 'var(--color-bg-3)',
        btn: {
          accent: 'var(--btn-accent)',
          'accent-hover': 'var(--btn-accent-hover)',
          'accent-text': 'var(--btn-accent-text)',
          'secondary-bg': 'var(--btn-secondary-bg)',
          'secondary-text': 'var(--btn-secondary-text)',
          'disabled-bg': 'var(--btn-disabled-bg)',
          'disabled-border': 'var(--btn-disabled-border)',
          'disabled-text': 'var(--btn-disabled-text)',
          'tertiary-border': 'var(--btn-tertiary-border)',
          'tertiary-text': 'var(--btn-tertiary-text)',
          'tertiary-hover-bg': 'var(--btn-tertiary-hover-bg)',
          'ghost-disabled-border': 'var(--btn-ghost-disabled-border)',
          'ghost-disabled-text': 'var(--btn-ghost-disabled-text)',
        },
        gpx_porto: gpxPorto,
        gpx_gray: {
          100: '#6C7789',
          200: '#818C9C',
          300: '#98A1AE',
          400: '#AFB6C0',
          500: '#C0C5CE',
          600: '#D1D5DB',
          700: '#DDE0E4',
          800: '#E8EAED',
          900: '#F4F5F6',
        },
        gpx_black: {
          50: '#1A1D23',
          100: '#1D222A',
          200: '#212731',
          300: '#232B38',
          400: '#323B48',
          500: '#3C4553',
        },
        gpx_terracotta: {
          100: '#E0352E',
          200: '#E24640',
          300: '#E55852',
          400: '#E86964',
          500: '#EA7A75',
          600: '#EE9490',
          700: '#F2AFAB',
          800: '#F6C8C6',
          900: '#FAE2E0',
        },
        gpx_slate: {
          100: '#49576E',
          200: '#52617A',
          300: '#5A6B87',
          400: '#627492',
          500: '#788AA5',
          600: '#919FB6',
          700: '#A9B4C6',
          800: '#C2C9D6',
          900: '#CED4DE',
        },
        gpx_red: {
          100: '#562725',
          150: '#C21B10',
          200: '#E0443E',
          300: '#FCEEED',
        },
        gpx_green: {
          100: '#1B3918',
          200: '#58CE50',
          300: '#EAFBE9',
        },
        gpx_yellow: {
          100: '#58310E',
          200: '#FBBF24',
          280: '#FFE870',
          300: '#FCF7E3',
        },
        gpx_blue: {
          100: '#0E3758',
          200: '#1791F2',
          300: '#E3F1FC',
        },
        accent: {
          DEFAULT: 'var(--accent)',
          light: 'var(--accent-light)',
        },
      },
      fontSize: {
        lgmx_11: ['11px', { lineHeight: '12px', letterSpacing: '0.03em' }],
        lgmx_15: ['15px', { lineHeight: '16px', letterSpacing: '0.03em' }],
        lgmx_19: ['19px', { lineHeight: '20px', letterSpacing: '0.03em' }],
        gpx_xs: ['12px', { lineHeight: '18px', letterSpacing: '0.01em' }],
        gpx_sm: ['14px', { lineHeight: '20px', letterSpacing: '0.01em' }],
        gpx_base: ['16px', { lineHeight: '24px', letterSpacing: '0.01em' }],
        gpx_md: ['18px', { lineHeight: '24px', letterSpacing: '0.01em' }],
        gpx_lg: ['20px', { lineHeight: '28px', letterSpacing: '0.01em' }],
        gpx_xl: ['22px', { lineHeight: '1rem', letterSpacing: '0.01em' }],
        gpx_2xl: ['24px', { lineHeight: '32px', letterSpacing: '0.01em' }],
        gpx_3xl: ['28px', { lineHeight: '32px', letterSpacing: '0.01em' }],
        gpx_button: ['14px', { lineHeight: '24px', letterSpacing: '0.01em' }],
        gpx_label: ['12px', { lineHeight: '20px', letterSpacing: '0.02em' }],
      },
      dropShadow: {
        main: '0 4px 16px rgba(0, 0, 0, 0.02)',
        ui: '2px 4px 16px #F3F4F7',
        'ui-inversed': '2px -4px 16px #F3F4F7',
      },
      ringWidth: {
        3: '3px',
      },
      backgroundImage: {
        'maintenance-pattern': "url('/src/assets/maintenance-pattern.svg')",
      },
    },
  },
  plugins: [
    tailwindForms({ strategy: 'class' }),
    tailwindCss3d,
    typography,
    tailwindcssPrimeui,
  ],
};
